/// <reference types="google-publisher-tag" />

'use client'

import { useFeatureFlag } from '@posthog/next'
import { X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import AdSlot from '@/app/[locale]/(worlds)/[roomId]/components/AdSlot'
import { Button } from '@/components/ui/button'
import { useUAContext } from '@/context/UAContext'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { useFullscreenStatus } from '@/hooks/useFullscreenStatus'
import { usePlatformAdFree } from '@/hooks/usePlatformAdFree'
import { env } from '@/lib/runtime-env'
import { parseAdRefreshIntervalMs } from '@/schemas/ad-refresh'
import { parseAdTelemetryConfig } from '@/schemas/ad-telemetry'
import { type AdTelemetryConfig } from '@/types/ad-telemetry'

const STICKY_AD_SIZES = {
  desktop: [
    [970, 90],
    [728, 90],
  ] as googletag.MultiSize,
  mobile: [
    [320, 100],
    [320, 50],
  ] as googletag.MultiSize,
}

const STICKY_AD_SIZE_MAPPING = [
  [[768, 0], STICKY_AD_SIZES.desktop],
  [[0, 0], STICKY_AD_SIZES.mobile],
] satisfies googletag.SizeMappingArray

const STICKY_AD_UNIT_PATH_ENV_KEYS = {
  home: {
    desktop: 'NEXT_PUBLIC_GAM_STICKY_BANNER_HOME_PC_UNIT_PATH',
    mobile: 'NEXT_PUBLIC_GAM_STICKY_BANNER_HOME_MB_UNIT_PATH',
  },
  'content-detail': {
    desktop: 'NEXT_PUBLIC_GAM_STICKY_BANNER_CONTENT_DETAIL_PC_UNIT_PATH',
    mobile: 'NEXT_PUBLIC_GAM_STICKY_BANNER_CONTENT_DETAIL_MB_UNIT_PATH',
  },
} as const

type StickyBottomBannerAdPageType = keyof typeof STICKY_AD_UNIT_PATH_ENV_KEYS

type StickyBottomBannerAdProps = {
  enable?: boolean
  pageType: StickyBottomBannerAdPageType
}

export default function StickyBottomBannerAd({
  enable = true,
  pageType,
}: StickyBottomBannerAdProps) {
  const { isMobile, isMobileLandscape } = useUAContext()
  const { isAdFree } = usePlatformAdFree()
  const enableFull3d = useEnableFull3d()
  const { isFullscreen } = useFullscreenStatus()
  const featureFlag = useFeatureFlag('sticky-bottom-ad')
  const [isStorageReady, setIsStorageReady] = useState(false)

  const [isDismissed, setIsDismissed] = useSessionStorage(
    'sticky-bottom-banner-ad-dismissed',
    false,
    { initializeWithValue: false },
  )

  const gamAdUnitPath = env(STICKY_AD_UNIT_PATH_ENV_KEYS[pageType][isMobile ? 'mobile' : 'desktop'])

  const shouldShowAd =
    featureFlag?.variant === 'test' &&
    !!gamAdUnitPath &&
    enable &&
    !isAdFree &&
    !enableFull3d &&
    !isDismissed &&
    isStorageReady &&
    !isFullscreen &&
    !isMobileLandscape

  useEffect(() => {
    setIsStorageReady(true)
  }, [])

  if (!shouldShowAd || !gamAdUnitPath) return null

  return (
    <StickyBottomBannerGptSlot
      gamAdUnitPath={gamAdUnitPath}
      isMobile={isMobile}
      isDismissed={isDismissed}
      setIsDismissed={setIsDismissed}
      telemetry={parseAdTelemetryConfig(featureFlag?.payload)}
      refreshIntervalMs={parseAdRefreshIntervalMs(featureFlag?.payload)}
    />
  )
}

type StickyBottomBannerGptSlotProps = {
  gamAdUnitPath: string
  isMobile: boolean
  isDismissed: boolean
  setIsDismissed: (value: boolean) => void
  telemetry: AdTelemetryConfig
  refreshIntervalMs: number | undefined
}

function StickyBottomBannerGptSlot({
  gamAdUnitPath,
  isMobile,
  isDismissed,
  setIsDismissed,
  telemetry,
  refreshIntervalMs,
}: StickyBottomBannerGptSlotProps) {
  const [hasRenderedAd, setHasRenderedAd] = useState(false)
  const t = useTranslations()

  if (isDismissed) return null

  return (
    <>
      <div
        data-component="sticky-bottom-banner-ad"
        className={`bg-vive-primary-80 fixed right-0 bottom-0 left-0 z-999 flex justify-center py-1 shadow-[0_-2px_6px_rgb(0_0_0/0.1)] ${
          hasRenderedAd ? 'visible' : 'invisible'
        }`}
      >
        <div className="relative w-fit">
          <AdSlot
            adSurface="sticky-bottom-banner"
            telemetry={telemetry}
            gamAdUnitPath={gamAdUnitPath}
            sizes={isMobile ? STICKY_AD_SIZES.mobile : STICKY_AD_SIZES.desktop}
            sizeMapping={STICKY_AD_SIZE_MAPPING}
            refreshIntervalMs={refreshIntervalMs}
            className="w-fit"
            slotClassName="px-2 leading-0"
            showAdLabel={false}
            onSlotRenderEnded={setHasRenderedAd}
          />

          {hasRenderedAd && (
            <span className="text-vive-primary-20 absolute bottom-0 left-full inline-flex w-fit text-[9px] leading-4 tracking-[0.15px] whitespace-nowrap md:text-[12px]">
              {t('recommend-worlds.advertisement')}
            </span>
          )}
        </div>
        <div className="bg-vive-primary-80 absolute top-0 right-0 z-1 hidden min-[1018px]:block">
          <Button
            aria-label={t('media-preview-modal.close')}
            className="bg-vive-primary-80 hover:bg-vive-primary-70 size-6 rounded-full text-white/80 hover:text-white focus-visible:ring-2 focus-visible:ring-white"
            onClick={() => setIsDismissed(true)}
            size="custom"
            type="button"
            variant="custom"
          >
            <X aria-hidden className="size-3.5" />
          </Button>
        </div>
        <div className="bg-vive-primary-80 absolute right-0 bottom-full z-1 -mb-px flex h-7 w-8 items-center justify-center rounded-t-sm min-[1018px]:hidden">
          <Button
            aria-label={t('media-preview-modal.close')}
            className="bg-vive-primary-80 hover:bg-vive-primary-70 size-6 rounded-full text-white/80 hover:text-white focus-visible:ring-2 focus-visible:ring-white"
            onClick={() => setIsDismissed(true)}
            size="custom"
            type="button"
            variant="custom"
          >
            <X aria-hidden className="size-3.5" />
          </Button>
        </div>
      </div>
      {hasRenderedAd && <div aria-hidden className="h-[186px] shrink-0 md:h-[126px]" />}
    </>
  )
}
